import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { timeZoneShortLabel } from '@evi/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft, Key, Plug, Phone, MessageSquare, Mail, ChevronRight, X,
} from 'lucide-react';
import {
  fetchMyPassListAccess,
  fetchAccount,
  ackRecipientDisclaimer,
  type PassListAccessRow,
  type AccountView,
} from '@/lib/api';
import { StatusPill } from '@/components/primitives';
import { DisclaimerModal } from '@/components/ui/DisclaimerModal';
import { PhoneCaptureModal } from '@/components/ui/PhoneCaptureModal';
import { weekdayShort } from '@/lib/time-format';
import { mediaForCharger } from '@/lib/chargerMedia';
import styles from './MyAccessPage.module.css';

interface Props { onNavigate: (path: string) => void }

/**
 * Cross-tenant view of chargers the caller has been added to as a
 * pass-list member. Bidirectional twin of /my-chargers/:id/contacts:
 * that surface is the OWNER seeing who they shared with; this is the
 * GUEST seeing where they have access.
 *
 * Empty state is the most common path — most drivers won't be on
 * anyone's pass list. The card surface only renders when the API
 * returns at least one row.
 *
 * Each card surfaces:
 *   - location_name + identity (helps the driver pick when they
 *     have access at multiple chargers in one building)
 *   - online status pill
 *   - markup pill ("Guest pricing +X%") so the driver can decide
 *     whether to use this charger over a public option
 *   - time-window summary in human copy ("Mon-Fri 6 PM - 9 PM" /
 *     "Always available")
 *   - tap-to-reveal owner contact action sheet (tel:/sms:/mailto:)
 *   - "Open station" CTA to start a session via the existing
 *     pre-session flow
 */
export function MyAccessPage({ onNavigate }: Props) {
  const { t } = useTranslation('myAccess');
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ['my-access'],
    queryFn: fetchMyPassListAccess,
    staleTime: 60_000,
  });
  const rows = data ?? [];

  // Recipient-side disclaimer gate. We block the list (overlay,
  // not unmount) when the caller has at least one shared charger
  // and hasn't yet acked the recipient-side disclaimer. The flag
  // is per-user, set once, lives on User.first_pass_list_recipient
  // _disclaimer_acked_at.
  const accountQ = useQuery({
    queryKey: ['account'],
    queryFn: fetchAccount,
    staleTime: 60_000,
  });
  // Phase 5.2: phone is also required for any recipient who's been
  // pass-list-added. We gate phone-capture FIRST (before the
  // disclaimer) because the disclaimer modal makes more sense once
  // basic account state is in order. Both gates fire ONLY for
  // GRANTED rows — they exist for pass-list recipients (the owner
  // may need to reach them; the peer-sharing disclaimer covers the
  // grant relationship). History-only visited rows imply no
  // relationship and must not extort a phone number just to view
  // the page (query-widening rule: the union widened what this list
  // returns, so every consumer keying on "non-empty" had to learn
  // the discriminator).
  const hasGrantedRows = rows.some((r) => r.source === 'granted');
  const needsPhone = !!accountQ.data
    && accountQ.data.phone === null
    && hasGrantedRows;
  const needsAck = !!accountQ.data
    && accountQ.data.phone !== null
    && accountQ.data.first_pass_list_recipient_disclaimer_acked_at === null
    && hasGrantedRows;

  const handleAck = async (): Promise<void> => {
    const next = await ackRecipientDisclaimer();
    qc.setQueryData<AccountView | undefined>(['account'], next);
  };

  const handlePhoneCaptured = async (phone: string): Promise<void> => {
    qc.setQueryData<AccountView | undefined>(['account'], (prev) =>
      prev ? { ...prev, phone } : prev,
    );
  };

  const [activeOwnerForId, setActiveOwnerForId] = useState<string | null>(null);
  const activeRow = rows.find((r) => r.charger_id === activeOwnerForId) ?? null;

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <button
          type="button"
          className={styles.backFab}
          aria-label={t('a11y.back')}
          onClick={() => onNavigate('/profile')}
        >
          <ArrowLeft size={16} />
        </button>
        <div className={styles.titleBlock}>
          <h1 className={styles.title}>{t('title')}</h1>
          <p className={styles.subtitle}>
            {isLoading
              ? t('subtitle.loading')
              : rows.length === 0
                ? t('subtitle.empty')
                : t('subtitle.sharedCount', { count: rows.length })}
          </p>
        </div>
      </header>

      {isLoading && (
        <div className={styles.list}>
          <div className={styles.cardSkeleton} />
          <div className={styles.cardSkeleton} />
        </div>
      )}

      {!isLoading && error && (
        <div className={styles.errorBox}>
          {error instanceof Error ? error.message : t('error.loadFailed')}
        </div>
      )}

      {!isLoading && !error && rows.length === 0 && (
        <div className={styles.empty}>
          <div className={styles.emptyIcon}>
            <Key size={40} />
          </div>
          <h2 className={styles.emptyTitle}>{t('empty.title')}</h2>
          <p className={styles.emptyDesc}>{t('empty.desc')}</p>
        </div>
      )}

      {!isLoading && !error && rows.length > 0 && (
        <div className={styles.list}>
          {rows.map((r) => (
            <AccessCard
              key={r.charger_id}
              row={r}
              onOpenStation={() =>
                onNavigate(`/charging-station/${r.location_id}?charger=${encodeURIComponent(r.identity)}`)}
              onShowOwner={() => setActiveOwnerForId(r.charger_id)}
            />
          ))}
        </div>
      )}

      {activeRow && activeRow.owner && (
        <OwnerSheet
          row={activeRow}
          onClose={() => setActiveOwnerForId(null)}
        />
      )}

      {/* Recipient-side phone-capture gate. Renders before the
          disclaimer (only one overlay at a time; needsAck is
          conditioned on phone !== null in the parent). Mandatory:
          no skip — the next interaction needs a phone on file. */}
      {needsPhone && (
        <PhoneCaptureModal
          title={t('phoneModal.title')}
          reason={<>{t('phoneModal.reason')}</>}
          onCapture={handlePhoneCaptured}
        />
      )}

      {/* Recipient-side disclaimer overlay. Renders on top of the
          list (which is already drawn underneath) so the dismiss-on-
          ack feels like a curtain lifting rather than a navigation.
          No cancel affordance: the recipient must ack to proceed. */}
      {needsAck && (
        <DisclaimerModal
          title={t('disclaimer.title')}
          body={
            // Twin disclaimer copy — recipient-side. Owner-side
            // counterpart lives at MyChargerDetailPage.tsx:504.
            // The two surfaces frame the same legal/expectation
            // paragraph for the two sides of the relationship; if
            // either edits, the other should match.
            <>
              EVI facilitates this connection. The agreement (times,
              pricing, expectations) is between you and the other
              driver. We process payments and surface contact info.
              We don&rsquo;t enforce or arbitrate.
            </>
          }
          onAck={handleAck}
        />
      )}
    </div>
  );
}

interface AccessCardProps {
  row: PassListAccessRow;
  onOpenStation: () => void;
  onShowOwner: () => void;
}

function AccessCard({ row, onOpenStation, onShowOwner }: AccessCardProps) {
  const { t, i18n } = useTranslation('myAccess');
  const onlineTone: 'good' | 'mute' = row.is_online ? 'good' : 'mute';
  const onlineLabel = row.is_online ? t('card.online') : t('card.offline');
  const markupPct = typeof row.guest_markup_bps === 'number'
    ? Math.round(row.guest_markup_bps / 100)
    : null;
  const windowsCopy = formatWindows(
    row.guest_time_windows, t, i18n.language, row.window_timezone ?? null,
  );
  const ownerLabel = row.owner?.name
    ?? row.owner?.email
    ?? t('owner.fallback');
  // Same hardware, same card art as the owner sees on My Chargers —
  // this is literally the same physical unit. Identical precedence to
  // MyChargersPage's ChargerCard: a real photo wins, then the model's
  // ambient loop, then the plug glyph for unmatched hardware.
  const media = mediaForCharger(row);
  const reduceMotion = typeof window !== 'undefined'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  return (
    <article className={styles.card}>
      <div className={styles.photo}>
        {row.photo_url ? (
          <img src={row.photo_url} alt="" className={styles.photoImg} loading="lazy" />
        ) : media ? (
          reduceMotion ? (
            <img src={media.poster} alt="" className={styles.photoImg} />
          ) : (
            <video
              className={styles.photoImg}
              src={media.loop}
              poster={media.poster}
              preload="metadata"
              autoPlay
              muted
              loop
              playsInline
            />
          )
        ) : (
          <div className={styles.photoFallback} aria-hidden>
            <Plug size={28} />
          </div>
        )}
        <span className={styles.photoOverlay}>
          <StatusPill tone={onlineTone} variant="solid" size="sm">{onlineLabel}</StatusPill>
        </span>
      </div>
      <div className={styles.cardBody}>
        <p className={styles.cardEyebrow}>{row.location_name}</p>
        <p className={styles.cardTitle}>{row.identity}</p>
        <div className={styles.cardPills}>
          {/* granted = on the owner's free pass list; visited =
              history-only (the guest has billed sessions here). */}
          <StatusPill tone={row.source === 'granted' ? 'good' : 'mute'} variant="soft" size="sm">
            {row.source === 'granted' ? t('card.sourceGranted') : t('card.sourceVisited')}
          </StatusPill>
          {markupPct !== null && (
            <StatusPill tone="accent" variant="soft" size="sm">
              {t('card.guestPricing', { pct: markupPct })}
            </StatusPill>
          )}
          {!row.is_open_to_guests && (
            <StatusPill tone="info" variant="soft" size="sm">
              {t('card.freePassOnly')}
            </StatusPill>
          )}
        </div>
        <p className={styles.windowCopy}>{windowsCopy}</p>

        <div className={styles.cardActions}>
          <button
            type="button"
            className={styles.openBtn}
            onClick={onOpenStation}
            disabled={!row.is_online}
          >
            {t('card.openStation')}
            <ChevronRight size={14} />
          </button>
          {/* Granted rows only: visited rows carry name but no
              email/phone (two-tier PII), so the owner sheet would be
              a dead end claiming "no phone on file" about a phone
              that exists but is deliberately withheld. */}
          {row.owner && row.source === 'granted' && (
            <button
              type="button"
              className={styles.ownerBtn}
              onClick={onShowOwner}
              aria-label={t('card.contact', { name: ownerLabel })}
            >
              {t('card.contact', { name: ownerLabel })}
            </button>
          )}
        </div>
      </div>
    </article>
  );
}

function OwnerSheet({
  row, onClose,
}: { row: PassListAccessRow; onClose: () => void }) {
  const { t } = useTranslation('myAccess');
  const owner = row.owner!;
  const initials = computeInitials(owner.name ?? owner.email);
  return (
    <div className={styles.sheetOverlay} role="dialog" aria-modal>
      <button
        type="button"
        className={styles.sheetBackdrop}
        aria-label={t('a11y.close')}
        onClick={onClose}
      />
      <div className={styles.sheet}>
        <header className={styles.sheetHeader}>
          <span className={styles.avatar} aria-hidden>{initials}</span>
          <div className={styles.sheetWho}>
            <p className={styles.sheetName}>{owner.name ?? t('owner.fallback')}</p>
            <p className={styles.sheetSecondary}>
              {t('owner.ownerOf', { identity: row.identity, location: row.location_name })}
            </p>
          </div>
          <button
            type="button"
            className={styles.sheetClose}
            onClick={onClose}
            aria-label={t('a11y.close')}
          >
            <X size={16} />
          </button>
        </header>
        <div className={styles.sheetActions}>
          {owner.phone ? (
            <>
              <a className={styles.sheetActionBtn} href={`tel:${owner.phone}`}>
                <Phone size={14} /> {t('owner.call')}
              </a>
              <a className={styles.sheetActionBtn} href={`sms:${owner.phone}`}>
                <MessageSquare size={14} /> {t('owner.text')}
              </a>
            </>
          ) : (
            <span className={styles.sheetNoPhone}>{t('owner.noPhone')}</span>
          )}
          {owner.email && (
            <a className={styles.sheetActionBtn} href={`mailto:${owner.email}`}>
              <Mail size={14} /> {t('owner.email')}
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

/** Two-letter initials helper — kept local to MyAccessPage. The
 *  contacts page has a near-identical helper; if a third caller
 *  appears we can lift to shared. */
function computeInitials(input: string | null): string {
  if (!input) return '·';
  const trimmed = input.trim();
  if (trimmed.includes('@')) return trimmed.slice(0, 2).toUpperCase();
  const parts = trimmed.split(/\s+/);
  if (parts.length >= 2) return (parts[0]!.charAt(0) + parts[1]!.charAt(0)).toUpperCase();
  return trimmed.slice(0, 2).toUpperCase();
}

/**
 * Format the guest_time_windows array into a human-readable summary.
 * Empty array → "Always available." Single window → "Mon-Fri 6 PM -
 * 9 PM" with day-range collapsing for runs (Mon, Tue, Wed → Mon-Wed).
 * Multiple windows → join with " · " between window summaries.
 *
 * Hour formatting uses 12h clock + AM/PM since that's the common
 * idiom for US-leaning EVI v2 audiences. Midnight = 12 AM, noon =
 * 12 PM. end_hour=24 renders as "midnight" so the upper bound is
 * unambiguous.
 */
function formatWindows(
  windows: Array<{ start_hour: number; end_hour: number; days_of_week: number[] }>,
  t: TFunction,
  locale: string,
  timeZone?: string | null,
): string {
  if (windows.length === 0) return t('windowSummary.alwaysAvailable');
  const parts: string[] = [];
  for (const w of windows) {
    if (w.days_of_week.length === 0) continue;
    parts.push(`${formatDayList(w.days_of_week, locale)} ${formatHour(w.start_hour, 'start', t)} - ${formatHour(w.end_hour, 'end', t)}`);
  }
  if (parts.length === 0) return t('windowSummary.alwaysAvailable');
  // The reader is routinely in a different city from the charger, so
  // the hours are meaningless without naming their clock. When the
  // location has NO timezone the gate really is running on UTC — say
  // "UTC", never a plausible local abbreviation, because a wrong
  // label here is the same lie the whole fix exists to remove.
  const suffix = timeZone
    ? (timeZoneShortLabel(timeZone, locale) ?? timeZone)
    : t('windowSummary.utcFallback');
  return `${parts.join(' · ')} ${suffix}`;
}

function formatDayList(days: number[], locale: string): string {
  const sorted = [...days].sort((a, b) => a - b);
  // Collapse runs of consecutive day-of-week numbers. Sun(0) - Sat(6).
  const groups: Array<[number, number]> = [];
  let i = 0;
  while (i < sorted.length) {
    const start = sorted[i]!;
    let end = start;
    while (i + 1 < sorted.length && sorted[i + 1]! === end + 1) {
      end = sorted[i + 1]!;
      i += 1;
    }
    groups.push([start, end]);
    i += 1;
  }
  return groups
    .map(([a, b]) => a === b ? weekdayShort(a, locale) : `${weekdayShort(a, locale)}-${weekdayShort(b, locale)}`)
    .join(', ');
}

function formatHour(h: number, side: 'start' | 'end', t: TFunction): string {
  // "midnight" is a word (translated); the 12h AM/PM clock format is
  // founder-directed and stays as-is.
  if (side === 'end' && h === 24) return t('windowSummary.midnight');
  if (h === 0) return '12 AM';
  if (h === 12) return '12 PM';
  if (h < 12) return `${h} AM`;
  return `${h - 12} PM`;
}
